import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";

import {
  setDteSystemLoading,
  setDteSystemSubmitting,
  setDteSystemError,
  setDteSystemStatus,
  resetDteSystemState,
} from "../../store/slices/dte/dteSystemSlice";

import DteSystemService from "../../services/dte/DteSystemService";
import { EnableContingencyPayload, DisableContingencyPayload } from "../../types/dte/system/DteSystemTypes";

import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";

export const useDteSystem = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { form, isLoading, isSubmitting, error } = useSelector(
    (state: RootState) => state.dteSystem
  );

  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  /**
   * ✅ Cargar estado del sistema DTE
   */
  const loadStatus = async () => {
    try {
      dispatch(setDteSystemLoading(true));
      showLoadingIndicator();

      const data = await DteSystemService.getStatus();
      dispatch(setDteSystemStatus(data));
      return data;
    } catch (err: any) {
      dispatch(setDteSystemError(err?.message || "No se pudo obtener el estado del sistema DTE"));
      handle(err);
      return null;
    } finally {
      dispatch(setDteSystemLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
   * 🔘 Activar modo contingencia manual
   */
  const enableContingency = async (payload: EnableContingencyPayload) => {
    try {
      dispatch(setDteSystemSubmitting(true));
      showLoadingIndicator();

      await DteSystemService.enableContingency(payload);
      showAlert("success", "Contingencia Activada", "El modo contingencia fue activado correctamente.");

      await loadStatus(); // ⭐ refrescar estado después
    } catch (err: any) {
      handle(err);
    } finally {
      dispatch(setDteSystemSubmitting(false));
      hideLoadingIndicator();
    }
  };

  /**
   * 🔄 Desactivar modo contingencia manual
   */
  const disableContingency = async (payload: DisableContingencyPayload) => {
    try {
      dispatch(setDteSystemSubmitting(true));
      showLoadingIndicator();

      await DteSystemService.disableContingency(payload);
      showAlert("success", "Contingencia Desactivada", "El modo contingencia fue desactivado correctamente.");

      await loadStatus(); // ⭐ refrescar estado después
    } catch (err: any) {
      handle(err);
    } finally {
      dispatch(setDteSystemSubmitting(false));
      hideLoadingIndicator();
    }
  };

  /**
   * 🚿 Reset
   */
  const reset = () => {
    dispatch(resetDteSystemState());
  };

  return {
    // State
    form,
    isLoading,
    isSubmitting,
    error,

    // Actions
    loadStatus,
    enableContingency,
    disableContingency,
    reset,
  };
};
